/**
 * ML opportunity data from the ML-powered recommendations API
 * Flexible interface to handle various API response formats
 *
 * Expected fields from the ML recommendations:
 * - lotId: Identifier for the lot/opportunity
 * - section: Section name (e.g., "101", "Floor")
 * - row: Row identifier (e.g., "A", "5")
 * - targetQuantity/quantity: Number of seats recommended
 * - seats/seatRange: Seat numbers or range (e.g., "1-4", "5,6,7")
 * - price: Recommended purchase price per ticket
 * - overallScore: ML confidence score (0-100)
 * - recommendation: Decision recommendation (e.g., "Buy", "Strong Buy")
 * - buyReasons: Explanation for the recommendation
 * - riskFactors: Identified risks for the opportunity
 * - predictedROI: Predicted return on investment percentage
 * - predictedDaysToSell: Estimated days until the lot sells
 */
export interface TopOpportunity {
    [key: string]: any;
}

/**
 * Historical sales record from RTP_SB_Inventory
 */
export interface HistorySaleRecord {
    inventory_id: number;
    eventId?: number;
    event: string;
    venue: string;
    event_date?: string;
    order_date?: string;
    listed_date?: string;
    quantity?: number;
    section: string;
    row: string;
    lowSeat: string;
    highSeat: string;
    seats: string;
    notes: string;
    internal_notes: string;
    tags: string;
    splitType: string;
    unitTicketSales?: number;  // Used as "Unit Ticket Sales" in the UI
    faceValue?: number;
    cost?: number;
    inserted_on: string;
    updated_on?: string;
    action?: number;
    original_price?: number;
    siteid: string;
    //
    order_number: number;
    invoiceId?: number;
    price: number;
    saleDate: string;
    listPrice?: number;
    profit: number;
}
/**
 * Purchase Seat Data Class
 * Represents purchased ticket data from AI_PurchasedTickets table.
 * This class encapsulates the data structure and the logic for creating instances.
 */
export class AIPurchasedTicket {
    /** [bigint][IDENTITY][NOT NULL] */
    readonly id?: bigint;

    // Optional fields for display purposes
    eventName?: string;
    venue?: string;
    eventDate?: string;
    siteId?: string;
    marketType?: string;
    onSaleDate?: string;
    city?: string;
    state?: string;
    eventCategoryName?: string;
    tags?: string;
    url?: string;
    
    /** [bigint][NOT NULL] */
    seatAnalysisResultId: bigint | undefined;

    /** [bigint][NULL] */
    singleAnalysisId?: bigint | null;

    /** [bigint][NOT NULL] */
    originalEventId: bigint;

    /** [nvarchar(100)][NOT NULL] */
    originalSeatId?: string;

    /** [nvarchar(100)][NULL] */
    originalSectionName?: string | null;

    /** [nvarchar(100)][NULL] */
    originalRowName?: string | null;

    /** [nvarchar(200)][NULL] */
    originalSeatRange?: string | null;

    /** [int][NULL] */
    originalQuantity?: number | null;

    /** [decimal(10,2)][NULL] */
    originalPricePerTicket?: number | null;

    /** [decimal(5,2)][NULL] */
    originalAIScore?: number | null;

    /** [nvarchar(100)][NULL] */
    purchasedSectionName: string | null;

    /** [nvarchar(100)][NULL] */
    purchasedRowName: string | null;

    /** [nvarchar(200)][NULL] */
    purchasedSeatRange: string | null;

    /** [int][NULL] */
    purchasedQuantity: number | null;

    /** [decimal(10,2)][NULL] */
    actualPricePerTicket: number | null;

    /** [computed: PurchasedQuantity * ActualPricePerTicket] */
    readonly totalPurchaseCost?: number | null;

    /** [nvarchar(50)][NOT NULL] */
    purchaseStatus: string;

    /** [datetime2(7)][NOT NULL] */
    purchaseDate?: string | Date;

    /** [nvarchar(100)][NULL] */
    purchasedBy_UserId: string | null;

    /** [nvarchar(100)][NULL] */
    updatedBy_UserId: string | null;

    /** [decimal(12,2)][NULL] */
    totalResaleRevenue?: number | null;

    /** [datetime2(7)][NULL] */
    lastSaleDate?: string | Date | null;

    /** [computed: (TotalResaleRevenue - cost) / cost] */
    readonly actualROI?: number | null;

    /** [nvarchar(max)][NULL] */
    supervisorNotes?: string | null;

    /** [computed: DATEDIFF(day, PurchaseDate, LastSaleDate)] */
    readonly daysToSell?: number | null;

    /** [nvarchar(max)][NULL] */
    ticketLink?: string | null;

    /** [nvarchar(max)][NULL] */
    publicNote?: string | null;

    /** [nvarchar(max)][NULL] */
    internalNote?: string | null;

    /** [decimal(10,2)][NULL] */
    sellingPrice?: number | null;

    /** [string][NULL] */
    orderNumber?: string | null;

    /** [nvarchar(max)][NULL] */
    account?: string | null;

    /** [int][NULL] */
    deliveryMethod?: number | null;

    /** [decimal(10,2)][NULL] Average days to sell */
    avgDaysToSell?: number | null;

    /** [bit][NULL] High risk flag for ticket purchases */
    highRisk?: boolean | null;

    /** [nvarchar(100)][NULL] Price level of the purchased tickets */
    priceLevel?: string | null;

    /** [nvarchar(max)][NULL] Notes about the purchased tickets */
    notes?: string | null;

    /** [nvarchar(max)][NULL] Lot attributes for the purchased tickets */
    lotAttributes?: string | null;

    /** [varchar(20)][NULL] Tracking version used for the ticket purchase */
    trackingVersion?: string | null;

    // Optional field for sales history data
    salesHistory?: HistorySaleRecord[];

    /**
   * The constructor is private to enforce object creation via static factory methods,
   * ensuring that instances are always created in a valid and consistent state.
   */
    private constructor (props: Partial<AIPurchasedTicket>) {
        this.originalEventId = props.originalEventId!;
        this.purchasedSectionName = props.purchasedSectionName!;
        this.purchasedRowName = props.purchasedRowName!;
        this.purchasedSeatRange = props.purchasedSeatRange!;
        this.purchasedQuantity = props.purchasedQuantity!;
        this.actualPricePerTicket = props.actualPricePerTicket!;
        this.purchaseStatus = props.purchaseStatus!;
        this.purchasedBy_UserId = props.purchasedBy_UserId!;
        this.updatedBy_UserId = props.updatedBy_UserId!;
        Object.assign(this, props);
    }

    /**
   * Converts a SeatRecommendation to an AIPurchasedTicket for initial creation.
   *
   * Conversion Logic:
   * - Original fields are populated from the seat analysis to preserve AI recommendations.
   * - Purchased fields are pre-filled with analysis values as a starting point for edits.
   * - purchaseStatus defaults to 'New', indicating a newly created purchase record.
   *
   * @param seat The seat recommendation to convert.
   * @returns A new instance of AIPurchasedTicket with pre-filled data.
   */
    public static fromSeatRecommendation (seat: SeatRecommendation): AIPurchasedTicket {
        return new AIPurchasedTicket({
            id: BigInt(0),
            seatAnalysisResultId: seat.id,
            singleAnalysisId: seat.analysisId ? BigInt(seat.analysisId) : null,
            originalEventId: seat.eventId,
            originalSeatId: seat.seatId,
            originalSectionName: seat.section || '',
            originalRowName: seat.row || '',
            originalSeatRange: seat.seatRange || '',
            originalQuantity: seat.quantity || 1,
            originalPricePerTicket: seat.price || null,
            originalAIScore: seat.scores?.overallScore || null,
            purchasedSectionName: seat.section || '',
            purchasedRowName: seat.row || '',
            purchasedSeatRange: seat.seatRange || '',
            purchasedQuantity: seat.quantity || 1,
            actualPricePerTicket: seat.price || null,
            purchaseStatus: 'New',
            purchaseDate: new Date().toISOString(),
            purchasedBy_UserId: null,
            totalPurchaseCost: seat.quantity && seat.price ? (seat.quantity * seat.price) : null,
            totalResaleRevenue: null,
            lastSaleDate: null,
            supervisorNotes: null,
            ticketLink: null,
            publicNote: null,
            internalNote: null,
            sellingPrice: null,
            orderNumber: null,
            account: null,
            deliveryMethod: 0,
            highRisk: null,
            priceLevel: seat.priceLevel || '',
            notes: seat.notes || '',
            lotAttributes: seat.lotAttributes || '',
            trackingVersion: null,
        });
    }

    /**
   * Creates an empty AIPurchasedTicket for manual entry.
   *
   * Default Values Explanation:
   * - purchaseStatus: 'New' - All new purchases start in 'New' status.
   * - purchasedQuantity: 1 - Most common case is buying 1 ticket.
   * - deliveryMethod: 0 - Standard delivery method as default.
   * - purchaseDate: Current timestamp in ISO format for database compatibility.
   *
   * @param originalEventId The ID of the event for which this manual ticket is being created.
   * @returns A new instance of AIPurchasedTicket with default values.
   */
    public static createEmpty (originalEventId: bigint, analysisId: bigint | null): AIPurchasedTicket {
        return new AIPurchasedTicket({
            id: BigInt(0),
            seatAnalysisResultId: undefined,
            singleAnalysisId: analysisId,
            originalEventId: originalEventId,
            originalSeatId: '',
            originalSectionName: null,
            originalRowName: null,
            originalSeatRange: null,
            originalQuantity: null,
            originalPricePerTicket: null,
            originalAIScore: null,
            purchasedSectionName: null,
            purchasedRowName: null,
            purchasedSeatRange: null,
            purchasedQuantity: 1,
            actualPricePerTicket: null,
            purchaseStatus: 'New',
            purchaseDate: new Date().toISOString(),
            purchasedBy_UserId: null,
            totalPurchaseCost: null,
            totalResaleRevenue: null,
            lastSaleDate: null,
            supervisorNotes: null,
            ticketLink: null,
            publicNote: null,
            internalNote: null,
            sellingPrice: null,
            orderNumber: null,
            account: null,
            deliveryMethod: 0,
            highRisk: null,
            priceLevel: null,
            notes: null,
            lotAttributes: null,
            trackingVersion: null,
        });
    }

    /**
     * Creates an AIPurchasedTicket from ML opportunity data.
     *
     * Mapping Logic:
     * - Original fields are set to null since ML opportunities don't have analysis data
     * - Purchased fields are populated from ML opportunity data
     * - purchaseStatus defaults to 'New' for all ML-generated opportunities
     * - Uses flexible mapping with fallbacks for various API response formats
     *
     * @param mlData The ML opportunity data to convert
     * @param eventId The event ID for the opportunity
     * @param analysisId The analysis ID (optional)
     * @returns A new instance of AIPurchasedTicket with ML data mapped to purchased fields
     */
    public static fromMLData (mlData: TopOpportunity, eventId: bigint, analysisId?: bigint | null): AIPurchasedTicket {
        return new AIPurchasedTicket({
            id: BigInt(0),
            singleAnalysisId: analysisId || null,
            originalEventId: eventId,
            purchasedSectionName: mlData.section || null,
            purchasedRowName: mlData.row || null,
            purchasedSeatRange: mlData.seats || mlData.seatRange || null,
            purchasedQuantity: mlData.targetQuantity || mlData.quantity || 1,
            actualPricePerTicket: mlData.price || null,
            purchaseStatus: 'New',
            purchaseDate: new Date().toISOString(),
            lotAttributes: JSON.stringify({ id: mlData.lotId }) || null,
            totalPurchaseCost: mlData.targetQuantity && mlData.price ? (mlData.targetQuantity * mlData.price) : null,
        });
    }
}

/**
 * Structure for seat recommendation data
 */
export class SeatRecommendation {
    public readonly id?: bigint;
    public readonly lotId?: string; // identifier for ml opportunity data
    public readonly eventId: bigint;
    public readonly seatId: string;
    public readonly analysisId?: number;
    public readonly section?: string;
    public readonly row?: string;
    public readonly seatRange?: string;
    public readonly quantity?: number;
    public readonly price?: number;
    public readonly priceLevel?: string;
    public readonly notes?: string;
    public readonly lotAttributes?: string;
    public readonly recommendation?: {
        decision: string;
        confidenceLevel?: string;
    };
    public readonly scores?: {
        overallScore: number;
        totalDefaultScore: number;
        totalRiskScore: number;
        totalPositiveScore: number;
    };
    public readonly scoreBreakdown?: ScoreCriterion[];
    public readonly factors?: {
        positiveFactors: string[];
        riskFactors: string[];
    };
    public readonly analysisTimestamp?: string;
    public readonly estimatedSalePrice?: number;
    public readonly purchaseStatus?: string;  // Purchase status from AI_PurchasedTickets table
    /**
     * Flag to identify records that were migrated from AIPurchasedTicket
     * When true, indicates this recommendation was converted from historical purchase data
     * rather than being generated from AI analysis.
     */
    public readonly isMigrated?: boolean;

    private constructor (init: Partial<SeatRecommendation>) {
        this.eventId = init.eventId!;
        this.seatId = init.seatId!;
        Object.assign(this, init);
    }

    /**
     * Creates a new SeatRecommendation instance from historical analyzed event data
     * @param data The raw data object for a single event
     */
    public static fromSeatAnalysisResult (data: AISeatAnalysisResult): SeatRecommendation {
        // Mapping snake_case to camelCase for clean code
        return new SeatRecommendation({
            id : data.id,
            eventId : data.eventId,
            seatId : data.seatId,
            analysisId : data.analysisId,
            section : data.sectionName || '',
            row : data.rowName || '',
            seatRange : data.seatRange || '',
            quantity : data.quantity || 0,
            price : data.price || 0,
            priceLevel : data.priceLevel || '',
            notes : data.notes || '',
            lotAttributes : data.lotAttributes || '',
            recommendation : {
                decision : data.recommendationDecision || '',
                confidenceLevel : data.recommendationConfidence || undefined,
            },
            scores : {
                overallScore : data.overallScore || 0,
                totalDefaultScore : 100 - (data.totalPositiveScore || 0) - (data.totalRiskScore || 0),
                totalRiskScore : data.totalRiskScore || 0,
                totalPositiveScore : data.totalPositiveScore || 0,
            },
            scoreBreakdown : data.scoreBreakdown ? JSON.parse(data.scoreBreakdown) : [],
            factors : {
                positiveFactors : data.positiveFactors ? JSON.parse(data.positiveFactors) : [],
                riskFactors : data.riskFactors ? JSON.parse(data.riskFactors) : [],
            },
            analysisTimestamp : data.analysisTimestamp || '',
            estimatedSalePrice : data.estimatedSalePrice || 0,
            purchaseStatus : data.purchaseStatus || '',
        });
    }

    /**
     * Creates a new SeatRecommendation instance from AIPurchasedTicket for migration purposes.
     *
     * Migration Logic:
     * - Converts historical purchase data to SeatRecommendation format when no analysis exists
     * - Uses "Symbolic" score (0) to indicate this is migrated data, not AI-analyzed
     * - Sets isMigrated flag to true for identification in UI
     * - Maps purchased seat details to recommendation structure
     *
     * @param ticket The purchased ticket data to convert
     * @returns A new SeatRecommendation instance marked as migrated
     */
    public static fromAIPurchasedTicket (ticket: AIPurchasedTicket): SeatRecommendation {
        return new SeatRecommendation({
            id: ticket.id,
            eventId: ticket.originalEventId,
            // Use a combination of purchased details as a unique seat identifier
            seatId: `migrated-${ticket.id || Date.now()}`,
            analysisId: ticket.singleAnalysisId ? Number(ticket.singleAnalysisId) : undefined,
            section: ticket.purchasedSectionName || '',
            row: ticket.purchasedRowName || '',
            seatRange: ticket.purchasedSeatRange || '',
            quantity: ticket.purchasedQuantity || 0,
            price: ticket.actualPricePerTicket || 0,
            priceLevel: ticket.priceLevel || '',
            notes: ticket.notes || '',
            lotAttributes: ticket.lotAttributes || '',
            recommendation: {
                decision: 'Migrate', // Indicate this is a migrated record
                confidenceLevel: undefined,
            },
            scores: {
                overallScore: 0, // Symbolic score for migrated data
                totalDefaultScore: 0,
                totalRiskScore: 0,
                totalPositiveScore: 0,
            },
            scoreBreakdown: [],
            factors: {
                positiveFactors: [],
                riskFactors: [],
            },
            analysisTimestamp: ticket.purchaseDate?.toString() || '',
            estimatedSalePrice: ticket.sellingPrice || 0,
            purchaseStatus: ticket.purchaseStatus,
            isMigrated: true, // Flag to identify this as migrated data
        });
    }
}
export interface ScoreCriterion {
    criteriaName: string;
    score: number;
    maxScore: number;
}
/**
 * Structure for historical AI seat analysis result data
 */
export interface AISeatAnalysisResult {
    id: bigint;
    analysisId: number;
    seatId: string;
    eventId: bigint;
    seatRange: string | null;
    rowName: string | null;
    sectionName: string | null;
    price: number | null;
    quantity: number | null;
    priceLevel: string | null;
    notes: string | null;
    lotAttributes: string | null;
    analysisTimestamp: string; // Typically a string in ISO 8601 format (e.g., "2025-10-21T08:49:00Z")
    analysisStatus: string;
    modelVersion: string | null;
    overallScore: number | null;
    totalPositiveScore: number | null;
    totalRiskScore: number | null;
    estimatedSalePrice: number | null;
    confidenceLevel: number | null;
    recommendationDecision: string | null;
    recommendationConfidence: string | null;
    positiveFactors: string | null; // Often a JSON string, can be typed further e.g., string[]
    riskFactors: string | null;     // Often a JSON string, can be typed further e.g., string[]
    scoreBreakdown: string | null;  // Often a JSON string, can be typed further e.g., Record<string, number>
    userId: string | null;
    analysisSource: string | null;
    action: number;  // 0 = No Action, 1 = Purchased, 2 = Listed, 3 = Purchased and Listed
    purchaseStatus: string; // e.g., 'New', 'Buy', 'Active',...
    isActive: boolean;
}
export interface FilterValues {
    [key: string]: any;
}